'use client';

/**
 * A small photo for each stop in the list (48px).
 *
 *  1. A campsite or park we list → its own hero image (passed in by the page).
 *  2. A town or place → the lead image of its Wikipedia article, FREE-LICENSED
 *     ONLY (`pilicense=free`, i.e. Wikimedia Commons). Never a scraped or
 *     fair-use image.
 *  3. Nothing found → null, and the row shows a drawn placeholder.
 *
 * Lookups are cached for the session (memory + sessionStorage), so moving or
 * re-adding a stop never refetches.
 */
import { useEffect, useState } from 'react';
import type { TripStop } from './types';

/** undefined = still looking · null = no photo · string = URL */
export type StopPhotoMap = Record<string, string | null | undefined>;

const STATE_NAMES: Record<string, string> = {
  VIC: 'Victoria',
  NSW: 'New South Wales',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
};

/**
 * Wikipedia titles to try for a stop name, best first.
 * "Bright, VIC" → ["Bright, Victoria", "Bright"]; "Port Fairy" → ["Port Fairy"].
 * Coordinates and pin labels ("Dropped pin", "-37.8, 144.9") give nothing.
 */
export function photoTitles(name: string): string[] {
  const raw = (name || '').trim();
  if (!raw || /^-?\d/.test(raw) || /dropped pin|my location|^pin\b/i.test(raw)) return [];
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const base = parts[0];
  if (!base || base.length < 3) return [];
  const out: string[] = [];
  const state = parts.slice(1).map((p) => p.replace(/\s+\d{4}$/, '').toUpperCase()).find((p) => STATE_NAMES[p]);
  if (state) out.push(`${base}, ${STATE_NAMES[state]}`);
  out.push(base);
  return out;
}

const memory = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();
const STORE_KEY = 'tp-stop-photos-v1';

function readStore(): Record<string, string | null> {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeStore(key: string, value: string | null) {
  try {
    const all = readStore();
    all[key] = value;
    sessionStorage.setItem(STORE_KEY, JSON.stringify(all));
  } catch {
    /* private mode / quota: memory cache still works */
  }
}

async function fetchWikiThumb(title: string): Promise<string | null> {
  const url =
    'https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*&redirects=1' +
    `&prop=pageimages&piprop=thumbnail&pithumbsize=120&pilicense=free&titles=${encodeURIComponent(title)}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const pages = data?.query?.pages ? Object.values(data.query.pages) : [];
  const page = pages[0] as { thumbnail?: { source?: string }; missing?: string } | undefined;
  return page?.thumbnail?.source || null;
}

export function lookupPlacePhoto(name: string): Promise<string | null> {
  const titles = photoTitles(name);
  const key = titles[0];
  if (!key) return Promise.resolve(null);
  if (memory.has(key)) return Promise.resolve(memory.get(key) ?? null);
  const stored = readStore();
  if (key in stored) {
    memory.set(key, stored[key]);
    return Promise.resolve(stored[key]);
  }
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = (async () => {
    let found: string | null = null;
    for (const t of titles) {
      try {
        found = await fetchWikiThumb(t);
      } catch {
        found = null;
      }
      if (found) break;
    }
    memory.set(key, found);
    writeStore(key, found);
    inflight.delete(key);
    return found;
  })();
  inflight.set(key, p);
  return p;
}

/**
 * Photos for the given stops, keyed by stop id. `entityPhoto` returns the
 * listing's own image for stops that are campsites/parks we know.
 */
export function useStopPhotos(
  stops: TripStop[],
  entityPhoto?: (entityId: number) => string | undefined
): StopPhotoMap {
  const [found, setFound] = useState<Record<string, string | null>>({});
  const signature = stops.map((s) => `${s.id}:${s.name}:${s.entityId ?? ''}`).join('|');

  useEffect(() => {
    let cancelled = false;
    stops.forEach((s) => {
      if (typeof s.entityId === 'number' && entityPhoto?.(s.entityId)) return;
      const key = `${s.name}`;
      if (key in found) return;
      void lookupPlacePhoto(s.name).then((url) => {
        if (!cancelled) setFound((prev) => (prev[key] === url ? prev : { ...prev, [key]: url }));
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const out: StopPhotoMap = {};
  stops.forEach((s) => {
    const own = typeof s.entityId === 'number' ? entityPhoto?.(s.entityId) : undefined;
    if (own) out[s.id] = own;
    else if (!photoTitles(s.name).length) out[s.id] = null;
    else out[s.id] = s.name in found ? found[s.name] : undefined;
  });
  return out;
}

/**
 * The same photo, big enough for the trip hero (~420px wide, 2x screens).
 * Wikimedia thumbs ask for a wider rendition; our image CDN gets a wider
 * width parameter. Anything else is returned as is.
 */
export function heroPhotoUrl(url: string): string {
  if (/wikimedia\.org\/.+\/\d+px-/.test(url)) return url.replace(/\/\d+px-/, '/960px-');
  return url.replace(/([?&](?:w|width)=)\d+/g, '$1960');
}
